import React, { useState, useEffect } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';

interface LeadFormProps {
  data?: unknown;
  apiBaseUrl: string;
  sessionId: string;
  sessionKey: string;
  /** Callback for SSE events - routes to ChatEngine.handleSseEvent */
  onSseEvent: (event: { event: string; data: string }) => void;
  /** Language for form labels (default: 'en') */
  language?: string;
}

const translations = {
  en: {
    title: 'One Last Step',
    description: 'Leave us your details and a consultant will craft a tailored proposal just for you.',
    labels: {
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      phone: 'Phone',
      country: 'Country',
    },
    placeholders: {
      firstName: 'Enter your first name',
      lastName: 'Enter your last name',
      email: 'Enter your email address',
      phone: '+39 123 456 7890',
      country: 'Select your country',
    },
    validation: {
      firstNameRequired: 'First name is required',
      lastNameRequired: 'Last name is required',
      emailRequired: 'Email is required',
      emailInvalid: 'Please enter a valid email address',
      submitError: 'Failed to submit. Please try again.',
    },
    buttons: {
      submit: 'Continue',
      submitting: 'Submitting...',
    },
    submitted: {
      thanks: 'Thank you',
      preparing: 'We are preparing your proposal...',
      done: 'Your request has been received.',
    },
  },
  it: {
    title: 'Un Ultimo Passo',
    description: 'Lasciaci i tuoi recapiti e un consulente preparerà una proposta su misura per te.',
    labels: {
      firstName: 'Nome',
      lastName: 'Cognome',
      email: 'Email',
      phone: 'Telefono',
      country: 'Paese',
    },
    placeholders: {
      firstName: 'Inserisci il tuo nome',
      lastName: 'Inserisci il tuo cognome',
      email: 'Inserisci la tua email',
      phone: '+39 123 456 7890',
      country: 'Seleziona il tuo paese',
    },
    validation: {
      firstNameRequired: 'Il nome è obbligatorio',
      lastNameRequired: 'Il cognome è obbligatorio',
      emailRequired: "L'email è obbligatoria",
      emailInvalid: 'Inserisci un indirizzo email valido',
      submitError: 'Invio fallito. Riprova.',
    },
    buttons: {
      submit: 'Continua',
      submitting: 'Invio in corso...',
    },
    submitted: {
      thanks: 'Grazie',
      preparing: 'Stiamo preparando la tua proposta...',
      done: 'La tua richiesta è stata ricevuta.',
    },
  },
};

const countries = [
  { code: 'IT', name: { en: 'Italy', it: 'Italia' } },
  { code: 'DE', name: { en: 'Germany', it: 'Germania' } },
  { code: 'FR', name: { en: 'France', it: 'Francia' } },
  { code: 'ES', name: { en: 'Spain', it: 'Spagna' } },
  { code: 'GB', name: { en: 'United Kingdom', it: 'Regno Unito' } },
  { code: 'AT', name: { en: 'Austria', it: 'Austria' } },
  { code: 'CH', name: { en: 'Switzerland', it: 'Svizzera' } },
  { code: 'US', name: { en: 'United States', it: 'Stati Uniti' } },
  { code: 'OTHER', name: { en: 'Other', it: 'Altro' } },
];

export const LeadForm: React.FC<LeadFormProps> = ({
  apiBaseUrl,
  sessionId,
  sessionKey,
  onSseEvent,
  language = 'en',
}) => {
  const t = translations[language as keyof typeof translations] || translations.en;

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isStreamComplete, setIsStreamComplete] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (isStreamComplete) {
      const timer = setTimeout(() => setIsDismissed(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [isStreamComplete]);

  const validateEmail = (emailValue: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue);
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!firstName.trim()) {
      newErrors.firstName = t.validation.firstNameRequired;
    }

    if (!lastName.trim()) {
      newErrors.lastName = t.validation.lastNameRequired;
    }

    if (!email.trim()) {
      newErrors.email = t.validation.emailRequired;
    } else if (!validateEmail(email)) {
      newErrors.email = t.validation.emailInvalid;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!sessionId || !sessionKey) {
      setErrors({ submit: t.validation.submitError });
      return;
    }

    setIsSubmitting(true);
    setIsSubmitted(true);
    setErrors({});

    try {
      await fetchEventSource(`${apiBaseUrl}/v1/sessions/${sessionId}/lead`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          sessionKey,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          phone: phone.trim() || null,
          country: country || null,
        }),
        onmessage: (event) => {
          onSseEvent({ event: event.event ?? '', data: event.data });
        },
        onerror: (err) => {
          console.error('SSE error during lead submission:', err);
          throw err;
        },
      });

      setIsStreamComplete(true);
    } catch (error) {
      console.error('Failed to submit lead info:', error);
      setIsSubmitted(false);
      setErrors({
        submit: error instanceof Error ? error.message : t.validation.submitError,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isDismissed) {
    return null;
  }

  if (isSubmitted) {
    return (
      <div className="lead-form-container">
        <div className={`lead-form-card lead-form-submitted${isStreamComplete ? ' lead-form-done' : ''}`}>
          <div className="lead-form-submitted-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5"/>
            </svg>
          </div>
          <h3 className="lead-form-title">{t.submitted.thanks}, {firstName.trim()}!</h3>
          <p className="lead-form-description lead-form-preparing">
            {!isStreamComplete && <span className="lead-form-preparing-dot"/>}
            {isStreamComplete ? t.submitted.done : t.submitted.preparing}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="lead-form-container">
      <div className="lead-form-card">
        <h3 className="lead-form-title">{t.title}</h3>
        <p className="lead-form-description">{t.description}</p>

        <form onSubmit={handleSubmit} className="lead-form">
          <div className="form-group">
            <label htmlFor="lead-firstName" className="form-label">
              {t.labels.firstName} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="lead-firstName"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className={`form-input ${errors.firstName ? 'error' : ''}`}
              placeholder={t.placeholders.firstName}
              disabled={isSubmitting}
            />
            {errors.firstName && <span className="error-message">{errors.firstName}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="lead-lastName" className="form-label">
              {t.labels.lastName} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="lead-lastName"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className={`form-input ${errors.lastName ? 'error' : ''}`}
              placeholder={t.placeholders.lastName}
              disabled={isSubmitting}
            />
            {errors.lastName && <span className="error-message">{errors.lastName}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="lead-email" className="form-label">
              {t.labels.email} <span className="required">*</span>
            </label>
            <input
              type="email"
              id="lead-email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`form-input ${errors.email ? 'error' : ''}`}
              placeholder={t.placeholders.email}
              disabled={isSubmitting}
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="lead-phone" className="form-label">
              {t.labels.phone}
            </label>
            <input
              type="tel"
              id="lead-phone"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="form-input"
              placeholder={t.placeholders.phone}
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="lead-country" className="form-label">
              {t.labels.country}
            </label>
            <select
              id="lead-country"
              autoComplete="country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="form-input"
              disabled={isSubmitting}
            >
              <option value="">{t.placeholders.country}</option>
              {countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name[language as keyof typeof c.name] || c.name.en}
                </option>
              ))}
            </select>
          </div>

          {errors.submit && <div className="submit-error">{errors.submit}</div>}

          <button type="submit" className="submit-button" disabled={isSubmitting}>
            {isSubmitting ? t.buttons.submitting : t.buttons.submit}
          </button>
        </form>
      </div>
    </div>
  );
};
